import { useContext, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapLoading } from "../../App.styles";
import Circle from "../google-maps/Circle";
import CustomMarkerOverlay from "../CustomMarkerOverlay";
import { OrganizeContext } from "../../organize/Organize.context";
import theme from "../../themes";

/**
 * Renders Google Map markers and unlock radius circles for challenges (tasks) 
 * that have location data (latitude and longitude).
 * 
 * It consumes the `OrganizeContext` to fetch the list of challenges and 
 * filters out unique circles to prevent overlapping translucent radii.
 * 
 * @param {Object} props
 * @param {Function} [props.onMarkersLoaded] - Callback fired when the parsed markers array changes.
 * @returns {React.ReactElement} A fragment containing the custom markers and radius circles.
 */
const ChallengeMarkers = ({ onMarkersLoaded }) => {
    const { t } = useTranslation();
    const { challenges } = useContext(OrganizeContext)
    const { isLoading: isLoadingChallenges, tasks = [] } = challenges || {}

    // Get markers from tasks/challenges
    const markers = useMemo(() => {
        return tasks.filter(t => t.latitude && t.longitude).map(({ id, latitude, longitude, unlock_by_location_radius }) => ({
        id,
        lat: latitude,
        lng: longitude,
        locked: unlock_by_location_radius > 0,
        radius: unlock_by_location_radius,
        }))
    }, [tasks])

    // Filter to unique circles to prevent stacking multiple translucent 
    // circles at the same location, which causes them to render darker.
    const uniqueCircles = useMemo(() => {
        const seen = new Set();
        return markers.filter(m => {
            if (!m.lat || !m.lng || !m.locked) return false;
            const key = `${m.lat}-${m.lng}-${m.radius}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [markers]);

    useEffect(() => {
        if (onMarkersLoaded && markers) {
            onMarkersLoaded(markers);
        }
    }, [markers, onMarkersLoaded]);

    return (
        <>
            {(isLoadingChallenges) && (
                <MapLoading>
                    {t(`Loading tasks...`)}
                </MapLoading>
            )}
            {markers.map((location) => {
                const { id, lat, lng, locked } = location;
                return (
                    <CustomMarkerOverlay
                        key={`${id}`} 
                        position={{ lat, lng }}
                        id={id}
                        locked={locked}
                        isDynamicIcon={true}
                    />
                )
            })}
            {uniqueCircles.map((circle) => {
                const { lat, lng, radius } = circle;
                return (
                    <Circle
                        key={`${lat}-${lng}-${radius}`}
                        center={{ lat, lng }}
                        radius={radius} // Google Maps uses meters for radius
                        options={{
                            strokeColor: theme.primaryColor,
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: theme.primaryColor,
                            fillOpacity: 0.35,
                        }}
                    />
                )
            })}
        </>
    )
}

export default ChallengeMarkers;